import { ContentDetailByPlaylist } from '@raydiant/api-client-js';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Popover } from '@headlessui/react';

import { PaginationProps } from '../../../components/Pagination/Pagination';
import ChartCard from '../ChartCard/ChartCard';
import QuestionMarkIcon from '../../../components/QuestionMarkIcon';
import DataTable from '../../../components/DataTable/DataTable';
import DataTableHeader, {
  TableSort,
} from '../../../components/DataTable/DataTableHeader';
import DataTableCell from '../../../components/DataTable/DataTableCell';
import { getDisplayValue } from '../ShopperEngagementInsights/utils';
import config from '../../../config';

const { supportAppUrl } = config;

const hoverClass = 'group-hover:bg-[#FEF8DF]';

type PlaylistsContentTableProps = PaginationProps & {
  playlistsData: ContentDetailByPlaylist[];
  presentationName: string;
  showPagination?: boolean;
  onSort: (sort: TableSort<ContentDetailByPlaylist>) => void;
  sort: TableSort<ContentDetailByPlaylist>;
};

export default function PlaylistsContentTable({
  playlistsData,
  presentationName,
  showPagination = true,
  currentPage,
  onPageChange,
  onSort,
  pageCount,
  sort,
}: PlaylistsContentTableProps) {
  const { t } = useTranslation();

  const renderDateTimeString = (dateTime: string) => {
    const currentDate = new Date();
    const givenDate = new Date(dateTime);
    const givenTimeString = moment(dateTime).format('HH:mm');
    const givenDateString = moment(givenDate).format('L');
    const isDateEqual = moment(currentDate).format('L') === givenDateString;

    if (!isDateEqual) {
      return (
        <>
          <div>{givenTimeString}</div>
          <div>{`(${givenDateString})`}</div>
        </>
      );
    }

    return givenTimeString;
  };
  return (
    <ChartCard
      subTitle={''}
      title={
        <div className="flex gap-3 items-center mb-3">
          <h2 className="text-black text-xl font-bold">
            {t('analytics.contentDetailPage.playlistsTable.title')}{' '}
            {presentationName}
          </h2>
          <Popover className="relative">
            <Popover.Button>
              <QuestionMarkIcon />
            </Popover.Button>
            <Popover.Panel className="absolute z-10 card popover">
              <div className="overflow-hidden">
                <p className="mb-3">
                  {t('analytics.contentDetailPage.playlistsTable.tooltip')}
                </p>
                <a
                  href={`${supportAppUrl}/visitor-insight/5imknKqrccCD9gmD6gKuJ1/how-do-i-use-the-insight-analytics-platform/6RjNYsyCvxraPhuSNUMYQo`}
                  rel="noreferrer"
                  target="_blank"
                >
                  {t('analytics.learnmore')}
                </a>
              </div>
            </Popover.Panel>
          </Popover>
        </div>
      }
      showTimeInterval={false}
    >
      <DataTable<ContentDetailByPlaylist>
        currentPage={currentPage}
        data={playlistsData}
        showPagination={showPagination}
        headers={
          <tr>
            <DataTableHeader<ContentDetailByPlaylist>
              className="w-96 !text-left sticky left-0 bg-white"
              onSort={onSort}
              showSort={sort.key === 'playlistName'}
              sortDirection={
                sort.key === 'playlistName' ? sort.direction : undefined
              }
              sortKey="playlistName"
              wrapperClassName="!justify-start"
            >
              {t('analytics.contentDetailPage.tableHeaders.playlistName')}
            </DataTableHeader>
            <DataTableHeader<ContentDetailByPlaylist>
              showSort={sort.key === 'playbackDuration'}
              sortKey="playbackDuration"
              sortDirection={
                sort.key === 'playbackDuration' ? sort.direction : undefined
              }
              onSort={onSort}
            >
              {t('analytics.contentDetailPage.tableHeaders.playbackDuration')}{' '}
              (mins)
            </DataTableHeader>
            <DataTableHeader<ContentDetailByPlaylist>
              showSort={sort.key === 'lastTimePlayed'}
              sortKey="lastTimePlayed"
              sortDirection={
                sort.key === 'lastTimePlayed' ? sort.direction : undefined
              }
              onSort={onSort}
            >
              {t('analytics.contentDetailPage.tableHeaders.lastTimePlayed')}
            </DataTableHeader>
            <DataTableHeader<ContentDetailByPlaylist>
              showSort={sort.key === 'nOfPlays'}
              sortKey="nOfPlays"
              sortDirection={
                sort.key === 'nOfPlays' ? sort.direction : undefined
              }
              onSort={onSort}
            >
              {t('analytics.contentDetailPage.tableHeaders.nOfPlays')}
            </DataTableHeader>
            <DataTableHeader<ContentDetailByPlaylist>
              showSort={sort.key === 'ots'}
              sortKey="ots"
              sortDirection={sort.key === 'ots' ? sort.direction : undefined}
              onSort={onSort}
            >
              {t('analytics.contentDetailPage.tableHeaders.ots')}
            </DataTableHeader>
            <DataTableHeader<ContentDetailByPlaylist>
              showSort={sort.key === 'impressions'}
              sortKey="impressions"
              sortDirection={
                sort.key === 'impressions' ? sort.direction : undefined
              }
              onSort={onSort}
            >
              {t('analytics.contentDetailPage.tableHeaders.impressions')}
            </DataTableHeader>
            <DataTableHeader<ContentDetailByPlaylist>
              showSort={sort.key === 'verifiedViews'}
              sortKey="verifiedViews"
              sortDirection={
                sort.key === 'verifiedViews' ? sort.direction : undefined
              }
              onSort={onSort}
            >
              {t('analytics.contentDetailPage.tableHeaders.verifiedViews')}
            </DataTableHeader>
            <DataTableHeader<ContentDetailByPlaylist>
              showSort={sort.key === 'engagedViews'}
              sortKey="engagedViews"
              sortDirection={
                sort.key === 'engagedViews' ? sort.direction : undefined
              }
              onSort={onSort}
            >
              {t('analytics.contentDetailPage.tableHeaders.engagedViews')}
            </DataTableHeader>
            <DataTableHeader<ContentDetailByPlaylist>
              showSort={sort.key === 'verifiedViewsRate'}
              sortKey="verifiedViewsRate"
              sortDirection={
                sort.key === 'verifiedViewsRate' ? sort.direction : undefined
              }
              onSort={onSort}
            >
              {t('analytics.contentDetailPage.tableHeaders.verifiedViewsRate')}{' '}
              (%)
            </DataTableHeader>
            <DataTableHeader<ContentDetailByPlaylist>
              showSort={sort.key === 'engagedViewsRate'}
              sortKey="engagedViewsRate"
              sortDirection={
                sort.key === 'engagedViewsRate' ? sort.direction : undefined
              }
              onSort={onSort}
            >
              {t('analytics.contentDetailPage.tableHeaders.engagedViewsRate')}{' '}
              (%)
            </DataTableHeader>
            <DataTableHeader<ContentDetailByPlaylist>
              showSort={sort.key === 'avgViewDuration'}
              sortKey="avgViewDuration"
              sortDirection={
                sort.key === 'avgViewDuration' ? sort.direction : undefined
              }
              onSort={onSort}
            >
              {t('analytics.contentDetailPage.tableHeaders.avgViewDuration')}{' '}
              (sec)
            </DataTableHeader>
          </tr>
        }
        onPageChange={onPageChange}
        pageCount={pageCount}
        renderRow={(item, index) => (
          <tr key={index} className="group">
            <DataTableCell
              className={`sticky left-0 bg-white ${hoverClass}`}
              wrapperClassName="!text-left"
              hasSort
            >
              {item.playlistName}
            </DataTableCell>
            <DataTableCell hasSort className={`${hoverClass}`}>
              {getDisplayValue(
                Math.round((item.playbackDuration || 0) / 60),
                ' min',
              )}
            </DataTableCell>
            <DataTableCell hasSort className={`${hoverClass}`}>
              {renderDateTimeString(item.lastTimePlayed)}
            </DataTableCell>
            <DataTableCell hasSort className={`${hoverClass}`}>
              {getDisplayValue(item.nOfPlays)}
            </DataTableCell>
            <DataTableCell hasSort className={`${hoverClass}`}>
              {getDisplayValue(item.ots)}
            </DataTableCell>
            <DataTableCell hasSort className={`${hoverClass}`}>
              {getDisplayValue(item.impressions)}
            </DataTableCell>
            <DataTableCell hasSort className={`${hoverClass}`}>
              {getDisplayValue(item.verifiedViews)}
            </DataTableCell>
            <DataTableCell hasSort className={`${hoverClass}`}>
              {getDisplayValue(item.engagedViews)}
            </DataTableCell>
            <DataTableCell hasSort className={`${hoverClass}`}>
              {item.verifiedViews
                ? getDisplayValue(
                    Math.round((item.verifiedViewsRate || 0) * 10) / 10,
                    '%',
                  )
                : '-'}
            </DataTableCell>
            <DataTableCell hasSort className={`${hoverClass}`}>
              {item.verifiedViews
                ? getDisplayValue(
                    Math.round((item.engagedViewsRate || 0) * 10) / 10,
                    '%',
                  )
                : '-'}
            </DataTableCell>
            <DataTableCell hasSort className={`${hoverClass}`}>
              {getDisplayValue(item.avgViewDuration, ' sec')}
            </DataTableCell>
          </tr>
        )}
      />
    </ChartCard>
  );
}
